import { useCallback, useEffect, useId, useMemo } from 'react';
import { create } from 'zustand';
import { confirmAction } from '@/lib/confirm';

/**
 * Unsaved work, and not losing it.
 *
 * Every editor that holds a draft registers here while it differs from what was saved. The
 * places that would throw a draft away — a dashboard tab, "Back to roster", "← Accounts", the
 * console's rail, a route change, closing the tab — ask `confirmDiscard` first, and say nothing
 * at all when nothing is dirty, so the question only appears when there is something to lose.
 *
 * Dirtiness is "the reader changed something since the last save", not a field-by-field diff:
 * typing a value and typing it back still counts. That errs towards asking once too often,
 * which is the cheap mistake; the expensive one is a lost afternoon of rates and tours.
 */
interface DirtyState {
  sources: ReadonlySet<string>;
  set: (id: string, dirty: boolean) => void;
  clear: () => void;
}

export const useDirtyStore = create<DirtyState>((set) => ({
  sources: new Set(),
  set: (id, dirty) =>
    set((state) => {
      if (state.sources.has(id) === dirty) return state;
      const sources = new Set(state.sources);
      if (dirty) sources.add(id);
      else sources.delete(id);
      return { sources };
    }),
  clear: () => set({ sources: new Set() }),
}));

export const anyDirty = () => useDirtyStore.getState().sources.size > 0;

/**
 * Ask before throwing unsaved work away. Resolves true at once when nothing is dirty; otherwise
 * the reader decides, and leaving forgets every draft so the next screen does not ask again.
 */
export async function confirmDiscard(): Promise<boolean> {
  if (!anyDirty()) return true;
  const leave = await confirmAction({
    title: 'Leave without saving?',
    body: 'You have changes on this screen that have not been saved. Leaving now throws them away.',
    confirm: 'Leave without saving',
    cancel: 'Stay and save',
    tone: 'danger',
    feature: 'dirty.leave',
  });
  if (leave) useDirtyStore.getState().clear();
  return leave;
}

/**
 * One editor's draft. Spread `bind` on the form (or its root element): any typing, picking or
 * ticking inside marks it dirty, because `input` and `change` bubble. Edits made by a button —
 * adding a tour row, choosing a day's mode — call `markDirty` themselves. A successful save
 * calls `markClean`. Unmounting forgets the draft, since the draft is gone with it.
 */
export function useDirty() {
  const id = useId();
  const dirty = useDirtyStore((state) => state.sources.has(id));
  const setDirty = useDirtyStore((state) => state.set);

  const markDirty = useCallback(() => setDirty(id, true), [id, setDirty]);
  const markClean = useCallback(() => setDirty(id, false), [id, setDirty]);

  useEffect(() => () => setDirty(id, false), [id, setDirty]);

  const bind = useMemo(() => ({ onInput: markDirty, onChange: markDirty }), [markDirty]);

  return { dirty, markDirty, markClean, bind };
}
